// Load forecast dialog component

import React, { useState, useMemo } from 'react';
import {
  FolderOpenIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { ForecastProject } from '@/types/forecast';
import { useForecastProjects } from '@/hooks/useForecastProject';
import { Modal, ModalHeader, ModalContent, ModalFooter, PrimaryButton, SecondaryButton } from '@/components/ui';

interface LoadForecastDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (forecast: ForecastProject) => void;
}

export const LoadForecastDialog: React.FC<LoadForecastDialogProps> = ({
  open,
  onClose,
  onSelect
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'updatedAt' | 'createdAt'>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedForecast, setSelectedForecast] = useState<ForecastProject | null>(null);

  // Fetch saved forecasts
  const { data: forecasts = [], isLoading, error } = useForecastProjects();

  // Get all unique tags for filtering
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    forecasts.forEach((forecast) => {
      if (forecast.metadata?.tags) {
        forecast.metadata.tags.forEach((tag) => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [forecasts]);

  // Filter and sort forecasts
  const filteredForecasts = useMemo(() => {
    let filtered = [...forecasts];
    
    // Apply search filter
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(forecast => 
        forecast.name.toLowerCase().includes(query) ||
        forecast.sourceAnalysisName?.toLowerCase().includes(query) ||
        forecast.metadata?.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    // Apply tag filter
    if (selectedTags.length > 0) {
      filtered = filtered.filter(forecast =>
        selectedTags.every(tag => forecast.metadata?.tags?.includes(tag))
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'createdAt':
          comparison = new Date(a.metadata.createdAt).getTime() - new Date(b.metadata.createdAt).getTime();
          break;
        case 'updatedAt':
          comparison = new Date(a.metadata.updatedAt).getTime() - new Date(b.metadata.updatedAt).getTime();
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [forecasts, searchTerm, selectedTags, sortBy, sortOrder]);

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTags([]);
    setSortBy('updatedAt');
    setSortOrder('desc');
  };

  const handleLoad = () => {
    if (selectedForecast) {
      onSelect(selectedForecast);
      onClose();
      setSelectedForecast(null);
      setSearchTerm('');
      setSelectedTags([]);
    }
  };

  const handleClose = () => {
    setSelectedForecast(null);
    setSearchTerm('');
    setSelectedTags([]);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
      }
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays === 1) {
      return 'yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return formatDate(dateString);
    }
  };

  const getForecastStatusIcon = (forecast: ForecastProject) => {
    if (forecast.results && forecast.results.length > 0) {
      return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
    }
    return <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />;
  };

  const getGranularityLabel = (months: number) => {
    if (months === 1) return 'Monthly';
    if (months === 3) return 'Quarterly';
    if (months === 12) return 'Yearly';
    return `${months} months`;
  };

  return (
    <Modal isOpen={open} onClose={handleClose} variant="workspace" size="xl" backdrop="blur">
      <ModalHeader
        icon={<FolderOpenIcon className="w-6 h-6 text-blue-600" />}
        title="Load Saved Forecast"
        subtitle="Select a previously saved forecast to restore its configuration and results."
        onClose={handleClose}
      />

      {/* Search and Filter Bar */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 space-y-4">
        {/* Search and Filter Controls */}
        <div className="flex items-center space-x-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search forecasts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
              showFilters || selectedTags.length > 0
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="w-4 h-4" />
            <span>Filters</span>
            {selectedTags.length > 0 && (
              <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {selectedTags.length}
              </span>
            )}
          </button>

          {/* Sort Options */}
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field as 'name' | 'updatedAt' | 'createdAt');
              setSortOrder(order as 'asc' | 'desc');
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="updatedAt-desc">Recently Modified</option>
            <option value="createdAt-desc">Recently Created</option>
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
          </select>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700">Filter by Tags</h4>
              {selectedTags.length > 0 && (
                <button
                  onClick={() => setSelectedTags([])}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              )}
            </div>
            {allTags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => handleTagToggle(tag)}
                    className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                      selectedTags.includes(tag)
                        ? 'bg-blue-100 border-blue-300 text-blue-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No tags available</p>
            )}
          </div>
        )}
      </div>

      <ModalContent padding={false} className="overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading saved forecasts...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-red-600 font-medium mb-2">Error Loading Forecasts</p>
              <p className="text-gray-600">Please try again later.</p>
            </div>
          </div>
        ) : filteredForecasts.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <ArrowTrendingUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-900 font-medium mb-2">No Forecasts Found</p>
              <p className="text-gray-600 mb-4">
                {searchTerm || selectedTags.length > 0 
                  ? 'No forecasts match your search and filters.' 
                  : 'Calculate and save a forecast to see it here.'}
              </p>
              {(searchTerm || selectedTags.length > 0) && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto p-6">
            {filteredForecasts.map(forecast => (
              <div
                key={forecast.id}
                onClick={() => setSelectedForecast(forecast)}
                className={`bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer ${
                  selectedForecast?.id === forecast.id
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-medium text-gray-900">{forecast.name}</h3>
                      {getForecastStatusIcon(forecast)}
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-1">
                      Source: <span className="font-medium">{forecast.sourceAnalysisName}</span>
                    </p>
                    
                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center">
                        <CalendarIcon className="w-3 h-3 mr-1" />
                        {forecast.configuration.forecastParams.horizonYears} year horizon
                      </span>
                      <span>•</span>
                      <span className="flex items-center">
                        <ChartBarIcon className="w-3 h-3 mr-1" />
                        {forecast.results?.length || 0} periods
                      </span>
                      <span>•</span>
                      <span className="flex items-center">
                        <ClockIcon className="w-3 h-3 mr-1" />
                        {formatRelativeTime(forecast.metadata.createdAt)}
                      </span>
                      {forecast.configuration.forecastParams.granularityMonths && (
                        <>
                          <span>•</span>
                          <span>
                            {getGranularityLabel(forecast.configuration.forecastParams.granularityMonths)}
                          </span>
                        </>
                      )}
                    </div>
                    
                    {forecast.metadata.tags && forecast.metadata.tags.length > 0 && (
                      <div className="flex items-center space-x-1 mt-2">
                        <div className="flex space-x-1">
                          {forecast.metadata.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                          {forecast.metadata.tags.length > 3 && (
                            <span className="text-xs text-gray-400">
                              +{forecast.metadata.tags.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="text-right">
                    <div className="flex items-center space-x-2">
                      {/* Radio selection */}
                      <input
                        type="radio"
                        name="forecast"
                        checked={selectedForecast?.id === forecast.id}
                        onChange={() => setSelectedForecast(forecast)}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ModalContent>

      <ModalFooter justify="between">
        <div className="text-sm text-gray-600">
          {filteredForecasts.length} forecast{filteredForecasts.length !== 1 ? 's' : ''} 
          {(searchTerm || selectedTags.length > 0) && (
            <span className="text-blue-600"> (filtered)</span>
          )}
          {forecasts.length > filteredForecasts.length && (
            <span className="ml-1">of {forecasts.length} total</span>
          )}
        </div>
        
        <div className="flex items-center space-x-3">
          <SecondaryButton onClick={handleClose}>
            Cancel
          </SecondaryButton>
          
          <PrimaryButton
            onClick={handleLoad}
            disabled={!selectedForecast}
            icon={<FolderOpenIcon className="w-4 h-4" />}
          >
            Load Forecast
          </PrimaryButton>
        </div>
      </ModalFooter>
    </Modal>
  );
};
